/**
 * ListingInfo Component (Server)
 *
 * Diamond Architecture: Server Component for SEO.
 * Displays property attributes: beds, baths, size, floor level, pets.
 * Receives plain ViewModel types — no proto dependency.
 *
 * Renders ONLY what the owner STATED (LET-93). Every fact here is a claim
 * about the home, so an unstated fact renders no chip at all — never a
 * placeholder, never a default. Until epoch 33 the room counts arrived as
 * non-optional numbers, an unstated count arrived as `0`, and `bedrooms === 0`
 * rendered the customer's real two-bedroom flat as "Studio" with "0 Bath".
 * The decisions below narrow on `undefined` first, so that comparison cannot
 * be written against an absent value again.
 */
import { formatArea } from '@/shared/components/listings';
import { useLocale, useTranslations } from '@/shared/i18n';
import type { StatedPetsPolicy } from '@/shared/lib/types/listing';
import type { ListingDetailData } from '@/shared/lib/types/listing-detail';
import { type ProtoEnumLocale } from '@letsgethome/lib/proto-enums/common/labels_gen';
import { floorLevelLabel } from '@letsgethome/lib/proto-enums/listing/labels_gen';
import { assertNever } from '@letsgethome/lib/utils/enums';
import { plaintextString } from '@letsgethome/types/branded';
import { Bath, Bed, Building2, DoorOpen, PawPrint, Ruler } from 'lucide-react';

type Props = {
  listing: ListingDetailData;
};

/**
 * What the bedrooms chip may say. Three cases, and `unknown` is a case of its
 * own rather than a `0` — the shape the SSOT (`BedroomsDecision` in
 * `domain_core::listing_display`) already has server-side.
 */
export type BedroomsDecision =
  | { kind: 'unknown' }
  | { kind: 'studio' }
  | { kind: 'count'; count: number };

/** Narrow FIRST on absence, then on the studio rule. Exported for tests. */
export function decideBedrooms(bedrooms: number | undefined): BedroomsDecision {
  if (bedrooms === undefined) return { kind: 'unknown' };
  if (bedrooms === 0) return { kind: 'studio' };
  return { kind: 'count', count: bedrooms };
}

/** The pets chip's words for a STATED policy; exhaustive over the type. */
function petsPolicyKey(policy: StatedPetsPolicy): 'petsAllowed' | 'petsNotAllowed' {
  switch (policy) {
    case 'ALLOWED':
      return 'petsAllowed';
    case 'NOT_ALLOWED':
      return 'petsNotAllowed';
    default:
      return assertNever(policy);
  }
}

export function ListingInfo({ listing }: Props) {
  const t = useTranslations('listings.detail');
  const locale = useLocale() as ProtoEnumLocale;

  const bedrooms = decideBedrooms(listing.bedrooms);
  const bedroomLabel = (() => {
    switch (bedrooms.kind) {
      case 'unknown':
        return null;
      case 'studio':
        return t('studio');
      case 'count':
        return t('bedrooms', { count: bedrooms.count });
      default:
        return assertNever(bedrooms);
    }
  })();

  // A stated bathroom count renders as stated — a stated 0 is a fact too;
  // an unstated one renders nothing.
  const bathroomLabel =
    listing.bathrooms === undefined ? null : t('bathrooms', { count: listing.bathrooms });

  // LET-101: `optional int32` — absent = the owner never stated an area.
  // Narrow on `undefined`, never on `> 0`: until epoch 35 the field was a
  // required int32 and this guard was DECODING a sentinel, which is how a
  // never-stated area became a rendered area at all. There is no stated 0
  // to keep apart from silence — both write doors bound the field `gt: 0`.
  // Data shown verbatim (a formatted number + the localized unit), so it
  // crosses the i18n boundary as `plaintextString`, not as a bare string.
  const sizeLabel =
    listing.saleableArea === undefined
      ? null
      : plaintextString(`${formatArea(listing.saleableArea)} ${t('sqft')}`);

  // Floor level: render the localized label via the generated proto-enum
  // labels table. `floorLevel` is `undefined` when unset; `UNSPECIFIED`
  // (proto sentinel) is also skipped because there is no meaningful UI
  // string to display.
  const floorLevelText =
    listing.floorLevel && listing.floorLevel !== 'UNSPECIFIED'
      ? floorLevelLabel(listing.floorLevel, locale)
      : null;

  // Pets: only what the owner SAID. `undefined` is "did not say" and the
  // view-model type has no UNSPECIFIED, so there is no third branch to forget.
  const petsLabel = listing.petsPolicy === undefined ? null : t(petsPolicyKey(listing.petsPolicy));

  // Every fact unknown ⇒ no section: a heading over a row of nothing would
  // itself be a claim that there was something to know.
  // D12: the unit, only when the owner chose to show it (the wire omits it
  // otherwise). Owner-typed text, shown verbatim.
  const unitText = listing.unit === undefined ? null : plaintextString(listing.unit);

  const chips = [bedroomLabel, bathroomLabel, sizeLabel, floorLevelText, unitText, petsLabel];
  if (chips.every((chip) => chip === null)) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{t('propertyInfo')}</h3>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {bedroomLabel !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <Bed className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('bedroomsLabel')}</p>
              <p className="font-medium text-foreground">{bedroomLabel}</p>
            </div>
          </div>
        )}

        {bathroomLabel !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <Bath className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('bathroomsLabel')}</p>
              <p className="font-medium text-foreground">{bathroomLabel}</p>
            </div>
          </div>
        )}

        {sizeLabel !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <Ruler className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('sizeLabel')}</p>
              <p className="font-medium text-foreground">{sizeLabel}</p>
            </div>
          </div>
        )}

        {floorLevelText !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('floorLabel')}</p>
              <p className="font-medium text-foreground">{floorLevelText}</p>
            </div>
          </div>
        )}

        {unitText !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <DoorOpen className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('unitLabel')}</p>
              <p className="font-medium text-foreground">{unitText}</p>
            </div>
          </div>
        )}

        {petsLabel !== null && (
          <div className="flex items-center gap-3 rounded-lg border border-border p-4">
            <PawPrint className="h-5 w-5 text-muted-foreground" />
            <div>
              <p className="text-sm text-muted-foreground">{t('petsLabel')}</p>
              <p className="font-medium text-foreground">{petsLabel}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
